import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MessageSquare, Trash2, AlertCircle, ChevronRight, FileText, Loader2, RefreshCw, AlertTriangle, Shield, Zap, Lock } from 'lucide-react';
import { deleteScan, retryScan } from '../../hooks/useScans';
// Pattern type that can be either a string or an object with type/severity
type PatternItem = string | {
  type: string;
  severity?: string;
};
interface ScanCardProps {
  scanId: string;
  title: string;
  timeAgo: string;
  score: number;
  riskLevel: string;
  messages: number;
  tags?: string[];
  status?: 'uploading' | 'processing' | 'completed' | 'failed';
  patterns?: PatternItem[];
  summary?: string;
  isUnlocked?: boolean;
}
export function ScanCard({
  scanId,
  title,
  timeAgo,
  score,
  riskLevel,
  messages,
  tags = [],
  status = 'completed',
  patterns = [],
  summary,
  isUnlocked = true
}: ScanCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const isProcessing = status === 'uploading' || status === 'processing';
  // DEBUG: Log scan data to verify backend values
  console.log('[ScanCard] Render:', {
    scanId,
    score,
    riskLevel,
    status
  });
  // FIX: Use riskLevel prop directly instead of recalculating from score
  const getRiskBadgeStyle = () => {
    if (status === 'failed') return 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30';
    if (isProcessing) return 'bg-gray-800 text-gray-400 border-gray-700';
    // Use riskLevel from backend - DO NOT recalculate from score
    const normalizedRisk = riskLevel?.toLowerCase();
    if (normalizedRisk === 'high' || normalizedRisk === 'high risk') return 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30';
    if (normalizedRisk === 'medium' || normalizedRisk === 'medium risk') return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
    return 'bg-green-500/10 text-green-500 border-green-500/30';
  };
  // FIX: Use riskLevel prop directly instead of recalculating from score
  const getScoreColor = () => {
    const normalizedRisk = riskLevel?.toLowerCase();
    if (normalizedRisk === 'high' || normalizedRisk === 'high risk') return 'text-[#FF0000]';
    if (normalizedRisk === 'medium' || normalizedRisk === 'medium risk') return 'text-orange-500';
    return 'text-green-500';
  };
  // Helper to extract pattern info
  const getPatternInfo = (pattern: PatternItem) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM'
      };
    }
    return {
      type: pattern.type || 'Unknown',
      severity: pattern.severity || 'MEDIUM'
    };
  };
  // Helper for severity badge styling
  const getSeverityStyle = (severity: string) => {
    const s = severity?.toUpperCase();
    if (s === 'HIGH') return 'bg-[#FF0000]/20 text-[#FF0000] border-[#FF0000]/40';
    if (s === 'MEDIUM') return 'bg-orange-500/20 text-orange-400 border-orange-500/40';
    return 'bg-green-500/20 text-green-400 border-green-500/40';
  };
  // Helper for pattern icon
  const getPatternIcon = (type: string) => {
    const lower = type.toLowerCase();
    if (lower.includes('gaslight')) return AlertTriangle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('aggress')) return Zap;
    return AlertCircle;
  };
  // Get display patterns (up to 3)
  const displayPatterns = patterns.slice(0, 3);
  const hasMorePatterns = patterns.length > 3;
  const getStatusText = () => {
    if (status === 'uploading') return 'Uploading...';
    if (status === 'processing') return 'Processing...';
    if (status === 'failed') return 'Analysis Failed';
    return riskLevel;
  };
  const getStatusIcon = () => {
    if (status === 'failed') return <AlertCircle size={24} className="text-[#FF0000]" data-id="element-863" />;
    if (isProcessing) return <Loader2 size={24} className="text-[#FF0000] animate-spin" data-id="element-864" />;
    if (status === 'completed') return <FileText size={24} data-id="element-865" />;
    return <AlertCircle size={24} data-id="element-866" />;
  };
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this scan?')) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteScan(scanId);
    } catch (error) {
      console.error('Failed to delete scan:', error);
      alert('Failed to delete scan. Please try again.');
      setIsDeleting(false);
    }
  };
  const handleRetry = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRetrying(true);
    try {
      await retryScan(scanId);
    } catch (error) {
      console.error('Failed to retry scan:', error);
      alert('Failed to retry scan. Please try again.');
      setIsRetrying(false);
    }
  };
  // Skeleton Loader for Processing State
  if (isProcessing) {
    return <div className="relative bg-[#0a0a0a] border border-gray-800 p-5 flex items-center gap-5" data-id="element-867">
        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-700" data-id="element-868" />
        <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-700" data-id="element-869" />
        <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-700" data-id="element-870" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-700" data-id="element-871" />

        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gray-800 border border-gray-700" data-id="element-872">
          <Loader2 size={20} className="text-gray-600 animate-spin" data-id="element-873" />
        </div>
        <div className="flex-1 min-w-0 space-y-2" data-id="element-874">
          <div className="h-4 w-1/3 bg-gray-800 animate-pulse" data-id="element-875" />
          <div className="h-3 w-1/4 bg-gray-900 animate-pulse" data-id="element-876" />
        </div>
        <div className="text-sm font-mono text-gray-600 uppercase tracking-wide" data-id="element-877">
          Processing...
        </div>
      </div>;
  }
  // Failed State
  if (status === 'failed') {
    return <div className={`relative bg-[#0a0a0a] border-2 border-[#FF0000]/30 p-5 flex items-center gap-5 ${isDeleting || isRetrying ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-878">
        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#FF0000]" data-id="element-879" />
        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#FF0000]" data-id="element-880" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#FF0000]" data-id="element-881" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#FF0000]" data-id="element-882" />

        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000]" data-id="element-883">
          <AlertCircle size={24} data-id="element-884" />
        </div>

        <div className="flex-1 min-w-0" data-id="element-885">
          <div className="flex items-center gap-3 mb-2" data-id="element-886">
            <h4 className="text-base font-bold text-white truncate uppercase tracking-tight" data-id="element-887">
              {title}
            </h4>
            <span className="px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30" data-id="element-888">
              Analysis Failed
            </span>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500 font-mono" data-id="element-889">
            <span className="flex items-center gap-1.5" data-id="element-890">
              <Clock size={14} className="text-gray-600" data-id="element-891" />
              {timeAgo}
            </span>
            <span className="text-[#FF0000]" data-id="element-892">
              Forensic analysis could not be completed
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-893">
          <button onClick={handleRetry} disabled={isRetrying} className="flex items-center gap-2 px-4 py-2 bg-[#FF0000] text-white 
                     hover:bg-[#FF0000]/90 transition-all duration-200 font-bold text-sm uppercase tracking-wider
                     disabled:opacity-50 disabled:cursor-not-allowed" title="Retry analysis" data-id="element-894">
            <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} data-id="element-895" />
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>

          <button onClick={handleDelete} disabled={isDeleting} className="p-2 text-gray-600 hover:text-[#FF0000] hover:bg-[#FF0000]/10 
                     transition-all duration-200" title="Delete scan" aria-label="Delete scan" data-id="element-896">
            <Trash2 size={18} data-id="element-897" />
          </button>
        </div>
      </div>;
  }
  // Completed State - UPDATED
  return <Link to={`/analysis?id=${scanId}`} className="block group" data-id="element-898">
      <div className={`relative bg-[#0a0a0a] border border-gray-800 p-5 hover:border-[#FF0000]/50 transition-all duration-200 ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-899">
        {/* Corner Brackets */}
        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-900" />
        <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-901" />
        <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-902" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-903" />

        <div className="flex items-start gap-5" data-id="element-904">
          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center transition-colors bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000] group-hover:bg-[#FF0000]/20" data-id="element-905">
            <FileText size={24} data-id="element-906" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-907">
            {/* Title Row */}
            <div className="flex items-center gap-3 mb-2" data-id="element-908">
              <h4 className="text-base font-bold text-white truncate group-hover:text-[#FF0000] transition-colors uppercase tracking-tight" data-id="element-909">
                {title}
              </h4>
              <span className={`px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest flex-shrink-0 ${getRiskBadgeStyle()}`} data-id="element-910">
                {getStatusText()}
              </span>
              {!isUnlocked && status === 'completed' && <span className="px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest flex-shrink-0 bg-gray-800 text-gray-400 border-gray-700" data-id="element-911">
                  <Lock size={10} className="inline mr-1" data-id="element-912" />
                  Locked
                </span>}
            </div>

            {/* Meta Row */}
            <div className="flex items-center gap-4 text-xs text-gray-500 font-mono mb-3" data-id="element-913">
              <span className="flex items-center gap-1.5" data-id="element-914">
                <Clock size={14} className="text-gray-600" data-id="element-915" />
                {timeAgo}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-916">
                <MessageSquare size={14} className="text-gray-600" data-id="element-917" />
                {messages} intercepts
              </span>
            </div>

            {/* Patterns Section */}
            {displayPatterns.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-918">
                {displayPatterns.map((pattern, idx) => {
              const info = getPatternInfo(pattern);
              const PatternIcon = getPatternIcon(info.type);
              return <div key={idx} className={`inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-mono uppercase tracking-wide ${getSeverityStyle(info.severity)}`} data-id="element-919">
                      <PatternIcon size={12} data-id="element-920" />
                      <span className="truncate max-w-[100px]" data-id="element-921">
                        {info.type}
                      </span>
                    </div>;
            })}
                {hasMorePatterns && <span className="text-[10px] font-mono text-gray-500" data-id="element-922">
                    +{patterns.length - 3} more
                  </span>}
              </div>}

            {/* Summary Message - Show AI-generated summary if available */}
            {summary && <p className="text-xs text-gray-400 line-clamp-2 font-mono leading-relaxed mb-3" data-id="element-923">
                {summary}
              </p>}

            {/* Locked State CTA */}
            {!isUnlocked && status === 'completed' && <div className="mt-3" data-id="element-924">
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30 hover:bg-[#FF0000] hover:text-white transition-all font-bold text-xs uppercase tracking-wider" data-id="element-925">
                  <Lock size={12} data-id="element-926" />
                  View Full Report
                </button>
              </div>}
          </div>

          {/* Right Side - Score & Actions */}
          <div className="flex items-center gap-6 flex-shrink-0" data-id="element-927">
            <div className="text-right hidden sm:block" data-id="element-928">
              <div className={`text-2xl font-black leading-none font-mono ${getScoreColor()}`} data-id="element-929">
                {score}
              </div>
              <div className="text-[10px] font-mono text-gray-600 uppercase tracking-widest mt-1" data-id="element-930">
                Threat Level
              </div>
            </div>

            <div className="flex items-center gap-1" data-id="element-931">
              <button onClick={handleDelete} disabled={isDeleting} className="p-2 text-gray-600 hover:text-[#FF0000] hover:bg-[#FF0000]/10 
                         transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100" title="Delete scan" aria-label="Delete scan" data-id="element-932">
                <Trash2 size={18} data-id="element-933" />
              </button>

              <div className="p-2 text-gray-700 group-hover:text-[#FF0000] transition-colors" data-id="element-934">
                <ChevronRight size={20} data-id="element-935" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>;
}